import { API_KEY_SCOPES } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { describeError } from '../lib/api.ts';
import { api, type ApiKeyView, type ApiKeyWithSecretView } from '../lib/client.ts';
import { useMembers } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

const QUERY = ['api-keys'] as const;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** Scopes grouped by resource, read first: `candidate.read` → Candidate: read, create, update. */
const ACTION_ORDER = ['read', 'create', 'update', 'upload'];
const rank = (scope: string) => {
  const i = ACTION_ORDER.indexOf(scope.split('.')[1]!);
  return i === -1 ? ACTION_ORDER.length : i;
};
const GROUPS = API_KEY_SCOPES.reduce<Array<{ resource: string; scopes: string[] }>>((out, s) => {
  const resource = s.split('.')[0]!;
  const group = out.find((g) => g.resource === resource);
  if (group) group.scopes.push(s);
  else out.push({ resource, scopes: [s] });
  return out;
}, []).map((g) => ({ ...g, scopes: [...g.scopes].sort((a, b) => rank(a) - rank(b)) }));
const label = (resource: string) =>
  resource.charAt(0).toUpperCase() + resource.slice(1).replace('_', ' ');

const EXPIRY = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: 'never', label: 'Never' },
] as const;
const GRACE = [
  { value: '0', label: 'Stop it now' },
  { value: '60', label: 'Keep it working for 1 hour' },
  { value: '1440', label: 'Keep it working for 1 day' },
] as const;

/**
 * Settings → API keys (TS-954): the organisation's keys for integrations. A key's secret is
 * shown once, when it is created or rotated; afterwards only its prefix is. Admins (holders of
 * `integration.manage`) create, edit, rotate and revoke; `integration.read` sees the list.
 */
export function ApiKeysPage() {
  const session = useSession();
  const qc = useQueryClient();
  const { nameOf } = useMembers();
  const keys = useQuery({ queryKey: QUERY, queryFn: api.apiKeys.list });
  const canManage = session.can('integration.manage');
  const [editing, setEditing] = useState<ApiKeyView | 'new' | null>(null);
  const [rotating, setRotating] = useState<ApiKeyView | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyView | null>(null);
  const [secret, setSecret] = useState<ApiKeyWithSecretView | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: QUERY });

  return (
    <Gate permission="integration.read" feature="api_keys.enabled">
      <PageHeader
        title="API keys"
        description="For integrations calling the API without a person signing in. A key can reach only the endpoints its scopes allow, and what it changes is recorded as the key."
        actions={
          canManage && (
            <Button onClick={() => setEditing('new')} data-testid="new-api-key">
              <Plus /> New API key
            </Button>
          )
        }
      />
      {keys.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(keys.error)}
        </p>
      )}
      {keys.data?.length === 0 && (
        <EmptyState
          icon={KeyRound}
          title="No API keys yet."
          description="Create one for each integration, with only the scopes it needs."
        />
      )}
      {keys.data && keys.data.length > 0 && (
        <div className="bg-card shadow-card rounded-card overflow-x-auto border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Scopes</th>
                <th className="px-3 py-2 font-medium">Limit</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium">Last used</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.data.map((k) => (
                <tr key={k.id} className="border-t align-top" data-testid="api-key-row">
                  <td className="px-3 py-2">
                    <p className="font-medium">{k.name}</p>
                    <p className="text-muted-foreground text-xs">
                      by {nameOf(k.createdBy)} · {day(k.createdAt)}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs">{k.prefix}_…</code>
                  </td>
                  <td className="px-3 py-2">
                    <p className="text-xs" title={k.scopes.join(', ')}>
                      {k.scopes.join(', ')}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums">
                    {k.effectiveRateLimitPerMinute}/min
                    {k.rateLimitPerMinute === null && (
                      <span className="text-muted-foreground"> (plan)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{day(k.expiresAt)}</td>
                  <td className="px-3 py-2 text-xs">{day(k.lastUsedAt)}</td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        k.status === 'active'
                          ? 'success'
                          : k.status === 'expired'
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      {k.status}
                    </Badge>
                    {k.previousSecretExpiresAt && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        Old secret works until{' '}
                        {new Date(k.previousSecretExpiresAt).toLocaleString()}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canManage && k.status === 'active' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(k)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRotating(k)}>
                          Rotate
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRevoking(k)}>
                          Revoke
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <KeyDialog
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (created) => {
            setEditing(null);
            if (created) setSecret(created);
            await refresh();
          }}
        />
      )}
      {rotating && (
        <RotateDialog
          apiKey={rotating}
          onClose={() => setRotating(null)}
          onRotated={async (rotated) => {
            setRotating(null);
            setSecret(rotated);
            await refresh();
          }}
        />
      )}
      {revoking && (
        <RevokeDialog
          apiKey={revoking}
          onClose={() => setRevoking(null)}
          onRevoked={async () => {
            setRevoking(null);
            await refresh();
          }}
        />
      )}
      {secret && <SecretDialog apiKey={secret} onClose={() => setSecret(null)} />}
      {session.can('activity.ingest') && <UnmatchedMessages />}
    </Gate>
  );
}

function KeyDialog({
  existing,
  onClose,
  onSaved,
}: {
  existing: ApiKeyView | null;
  onClose: () => void;
  onSaved: (created: ApiKeyWithSecretView | null) => Promise<void>;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [scopes, setScopes] = useState<ReadonlySet<string>>(new Set(existing?.scopes ?? []));
  const [expiry, setExpiry] = useState<string>('90');
  const [limit, setLimit] = useState(existing?.rateLimitPerMinute?.toString() ?? '');
  const save = useMutation({
    mutationFn: async () => {
      const rateLimitPerMinute = limit.trim() ? Number(limit) : null;
      const chosen = API_KEY_SCOPES.filter((s) => scopes.has(s));
      if (existing) {
        await api.apiKeys.patch(existing.id, {
          name: name.trim(),
          scopes: chosen,
          rateLimitPerMinute,
        });
        return null;
      }
      return api.apiKeys.create({
        name: name.trim(),
        scopes: chosen,
        rateLimitPerMinute,
        expiresAt:
          expiry === 'never'
            ? null
            : new Date(Date.now() + Number(expiry) * 86_400_000).toISOString(),
      });
    },
    onSuccess: onSaved,
  });
  const toggle = (s: string) =>
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  const ready = name.trim() && scopes.size > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : 'New API key'}</DialogTitle>
          <DialogDescription>
            Give the key only the scopes its integration needs. It can never manage people, roles,
            settings or other keys.
          </DialogDescription>
        </DialogHeader>
        <form
          id="api-key-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input
              aria-label="Key name"
              placeholder="e.g. RecruitCRM sync"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <fieldset className="flex flex-col gap-2" data-testid="scopes">
            <legend className="mb-1 text-sm">Scopes</legend>
            {GROUPS.map((g) => (
              <div key={g.resource} className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-muted-foreground w-28 text-xs">{label(g.resource)}</span>
                {g.scopes.map((s) => (
                  <label key={s} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={scopes.has(s)}
                      onChange={() => toggle(s)}
                      aria-label={s}
                    />
                    {s.split('.')[1]}
                  </label>
                ))}
              </div>
            ))}
          </fieldset>
          <div className="flex flex-wrap gap-4">
            {!existing && (
              <label className="flex flex-col gap-1 text-sm">
                Expires
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger className="w-40" aria-label="Expires">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm">
              Requests per minute
              <Input
                aria-label="Requests per minute"
                type="number"
                min={1}
                placeholder="Plan default"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="w-40"
              />
            </label>
          </div>
          {save.isError && (
            <p className="text-danger text-sm" role="alert">
              {describeError(save.error)}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="api-key-form" disabled={!ready || save.isPending}>
            {existing ? 'Save' : 'Create key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RotateDialog({
  apiKey,
  onClose,
  onRotated,
}: {
  apiKey: ApiKeyView;
  onClose: () => void;
  onRotated: (rotated: ApiKeyWithSecretView) => Promise<void>;
}) {
  const [grace, setGrace] = useState<string>('60');
  const rotate = useMutation({
    mutationFn: () => api.apiKeys.rotate(apiKey.id, Number(grace)),
    onSuccess: onRotated,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate {apiKey.name}</DialogTitle>
          <DialogDescription>
            A new secret for the same key: its scopes, history and webhook subscriptions stay as
            they are. Give the integration time to switch before the old secret stops working.
          </DialogDescription>
        </DialogHeader>
        <Select value={grace} onValueChange={setGrace}>
          <SelectTrigger className="w-72" aria-label="Old secret">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRACE.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {rotate.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(rotate.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => rotate.mutate()} disabled={rotate.isPending}>
            Rotate secret
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({
  apiKey,
  onClose,
  onRevoked,
}: {
  apiKey: ApiKeyView;
  onClose: () => void;
  onRevoked: () => Promise<void>;
}) {
  const revoke = useMutation({
    mutationFn: () => api.apiKeys.revoke(apiKey.id),
    onSuccess: onRevoked,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke {apiKey.name}?</DialogTitle>
          <DialogDescription>
            Every request with this key is refused from now on, including during any rotation grace
            period. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {revoke.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(revoke.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
            Revoke key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The one time a secret is visible. */
function SecretDialog({ apiKey, onClose }: { apiKey: ApiKeyWithSecretView; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey.secret);
      setCopied(true);
    } catch (err) {
      // No clipboard (permissions, an insecure origin): the secret is selectable below.
      void err;
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" /> {apiKey.name}
          </DialogTitle>
          <DialogDescription>
            Copy this secret now and keep it somewhere safe. It is not stored and will not be shown
            again; if it is lost, rotate the key.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <code
            className="bg-surface min-w-0 flex-1 rounded border px-2 py-1.5 text-xs break-all select-all"
            data-testid="api-key-secret"
          >
            {apiKey.secret}
          </code>
          <Button variant="outline" size="sm" onClick={() => void copy()} aria-label="Copy secret">
            {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">
          Send it as <code>Authorization: Bearer {apiKey.prefix}_…</code>
        </p>
        <DialogFooter>
          <Button onClick={onClose}>I have copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Emails and messages an integration posted that matched nobody (TS-955): the count, the most
 * recent, and a retry — run after the candidates they belong to have been added. Linking one
 * by hand is `POST /activities/events/{id}:link`.
 */
function UnmatchedMessages() {
  const qc = useQueryClient();
  const unmatched = useQuery({
    queryKey: ['ingest-unmatched'],
    queryFn: () => api.ingest.unmatched(),
  });
  const [result, setResult] = useState<string | null>(null);
  const rematch = useMutation({
    mutationFn: api.ingest.rematch,
    onSuccess: async (r) => {
      setResult(
        r.linked
          ? `Linked ${r.linked}; ${r.stillUnmatched} still unmatched.`
          : 'Nothing new matched.',
      );
      await qc.invalidateQueries({ queryKey: ['ingest-unmatched'] });
    },
  });
  if (!unmatched.data) return null;
  const { total, items } = unmatched.data;
  return (
    <section className="mt-10" data-testid="unmatched-messages">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold tracking-tight">Unmatched messages ({total})</h3>
          <p className="text-muted-foreground text-sm">
            Emails and messages an integration sent us whose address or number matched no candidate
            or contact.
          </p>
        </div>
        {total > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => rematch.mutate()}
            disabled={rematch.isPending}
          >
            Retry matching
          </Button>
        )}
      </div>
      {result && (
        <p className="text-muted-foreground mb-2 text-sm" role="status">
          {result}
        </p>
      )}
      {rematch.isError && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {describeError(rematch.error)}
        </p>
      )}
      {items.length > 0 && (
        <ul className="divide-border bg-card shadow-card rounded-card divide-y overflow-hidden border text-sm">
          {items.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap justify-between gap-2 px-4 py-3"
              data-testid="unmatched-event"
            >
              <span className="min-w-0 break-words">
                {e.summary}{' '}
                <span className="text-muted-foreground text-xs">
                  · {e.matchEmail ?? e.matchPhone ?? 'no address'} · via {e.source}
                </span>
              </span>
              <time className="text-muted-foreground text-xs" dateTime={e.occurredAt}>
                {new Date(e.occurredAt).toLocaleString()}
              </time>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
